import axios, { InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { store } from '../../store';
import { clearUser, setToken, setRefreshToken } from '../../store/slices/userSlice';
import { generateNonce } from '../utils';
import CryptoJS from 'crypto-js';

interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

interface ErrorResponse {
  message?: string;
  [key: string]: any;
}

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

const baseURL = import.meta.env.VITE_API_URL;

if (!baseURL) {
  throw new Error('VITE_API_URL environment variable is not set');
}

const axiosInstance = axios.create({
  baseURL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any = null, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Signature generation function
const generateSignature = (
  nonce: string,
  timestamp: number,
  method: string,
): string => {
  const secretKey = "drPYx8XSMDXd1V0IBSAEWJgIDVn5Uznh";
  
  // Create signature string
  const signatureString = `${nonce}:${timestamp}:${method}`;
    
  // Generate HMAC SHA-256 signature
  return CryptoJS.HmacSHA256(signatureString, secretKey).toString();
};

// Request interceptor
axiosInstance.interceptors.request.use(
  async (config: CustomAxiosRequestConfig) => {
    const token = store.getState().user.token;
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add timestamp header
    if (config.headers) {
      config.headers['X-Request-Timestamp'] = new Date().toISOString();
    }

    // Get nonce from existing function
    const nonce = await generateNonce();
    if (nonce) {
      config.headers['X-Nonce'] = nonce;
    }

    // Generate security headers
    const timestamp = Date.now();
    
    // Get the request data
    const method = config.method?.toUpperCase() || 'GET';
    
    // Generate signature
    const signature = generateSignature(
      nonce || '',
      timestamp,
      method
    );

    // Add security headers
    config.headers['x-request-id'] = nonce;
    config.headers['x-timestamp'] = timestamp;
    config.headers['x-signature'] = signature;

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError<ErrorResponse>) => {
    const originalRequest = error.config as CustomAxiosRequestConfig;
    if (!originalRequest) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return axiosInstance(originalRequest);
          })
          .catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = store.getState().user.refreshToken;
      if (!refreshToken) {
        store.dispatch(clearUser());
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        // Call refresh token endpoint directly
        const response = await axiosInstance.post('/users/refresh-token', {
          refreshToken,
        });
        const { accessToken, refreshToken: newRefreshToken } = response.data.data;
        
        store.dispatch(setToken(accessToken));
        store.dispatch(setRefreshToken(newRefreshToken));

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }

        processQueue(null, accessToken);
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        store.dispatch(clearUser());
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    if (error.response) {
      throw new ApiError(
        error.response.status,
        error.response.data?.message || 'An error occurred'
      );
    }
    throw new Error('Network error');
  }
);

export const handleApiError = (error: unknown): string => {
  if (error instanceof ApiError) {
    return error.message;
  }
  return 'An unexpected error occurred';
};

export default axiosInstance;